import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import { ThemeProvider } from "styled-components";

import { DotsLoader, Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import useDelayedFlag from "app/pages/sales-intelligence/hooks/useDelayedFlag";
import ChatMessage from "app/pages/sales-intelligence/modules/ai-agent/components/ChatMessage/ChatMessage";
import GradientButton from "app/pages/sales-intelligence/modules/ai-agent/components/GradientButton/GradientButton";
import { CHAT_THEME } from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/styles";
import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import useCreateThreadQuery from "app/pages/sales-intelligence/modules/ai-agent/queries/useCreateThreadQuery";
import useFeedbackMutation from "app/pages/sales-intelligence/modules/ai-agent/queries/useFeedbackMutation";
import { useGetCompanyInfo } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetCompanyInfo";
import { useGetThreadById } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetThreadById";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type {
    AiAgentMeetingContext,
    AiAgentMeetingPayload,
    FullThread,
} from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import type { FeedbackReaction } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import AIAgentChatFooter from "app/pages/sales-intelligence/pages/ai-agent/components/AIAgentChatFooter/AIAgentChatFooter";
import AIAgentReasoning from "app/pages/sales-intelligence/pages/ai-agent/components/AIAgentReasoning/AIAgentReasoning";
import { useFirstRespondForm } from "app/pages/sales-intelligence/pages/ai-agent/hooks/useFirstRespondForm";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import FirstRespondFormContent from "./FirstRespondFormContent";
import { StyledGradientButtonWrapper } from "./styles";

type Props = {
    onGenerate: () => void;
};

// Delay before showing the reasoning animation for a long-polling getThreadById, so opening an
// already-generated thread from history (fast GET) doesn't flash it.
const AWAITING_BRIEF_REASONING_DELAY_MS = 1000;

const FirstRespondForm: React.FC<Props> = ({ onGenerate }) => {
    const {
        params: { domain, id, autogenerate },
    } = useCurrentStateAndParams<{
        domain: string;
        id?: string;
        autogenerate: boolean;
    }>();
    const hasTriggeredAutogenerateRef = useRef(false);

    const queryClient = useQueryClient();

    const { data: companyData } = useGetCompanyInfo(domain);
    const companyName = companyData?.companyName ?? "";
    const mdComponentProps = useMemo(
        () => ({
            companyLogoSrc: companyData?.favicon ?? "",
        }),
        [companyData?.favicon],
    );

    const [isButtonAnimationEnded, setIsButtonAnimationEnded] = useState(false);
    const [firstFormPayload, setFirstFormPayload] = useState<AiAgentMeetingPayload | null>(null);
    const [reaction, setReaction] = useState<FeedbackReaction | undefined>(undefined);

    // need to have useIsFetching instead of isFetching because queryKey is not unique in this case
    const fetchingThreadGenerationCount = useIsFetching({ queryKey: aiAgentQueryKeys.generateThread(), exact: true });
    const isFetchingThreadGeneration = fetchingThreadGenerationCount > 0;

    const { data: historyThreadData, isFetching: isFetchingHistoryThread } = useGetThreadById({
        threadId: id,
        enabled: Boolean(id) && !isFetchingThreadGeneration,
    });

    // getThreadById long-polls while the thread is still generating, so a slow GET means the brief
    // isn't ready yet. Only treat it as "awaiting" after a short delay so opening an
    // already-generated thread from history (fast GET) doesn't flash the reasoning animation.
    const isAwaitingHistoryThreadBrief = useDelayedFlag(isFetchingHistoryThread, AWAITING_BRIEF_REASONING_DELAY_MS);

    const { data, threadId, messageId, isFetchedAfterMount } = useCreateThreadQuery(id ? null : firstFormPayload);

    const showAwaitingFirstAiBrief = !data && (isFetchingThreadGeneration || isAwaitingHistoryThreadBrief);

    const isDisabled = !!id || isFetchingThreadGeneration;

    const { mutate } = useFeedbackMutation({ isEmail: false });

    const { form, defaultCompetitors, createFirstFormPayload, createAutogeneratePayload, isLoading } =
        useFirstRespondForm(companyName, historyThreadData);

    useEffect(() => {
        if (threadId) {
            const payload: FullThread = {
                domain,
                meetingContext: firstFormPayload?.meetingContext as AiAgentMeetingContext,
                messages: [],
            };
            queryClient.setQueryData(aiAgentQueryKeys.threadById(threadId), payload);
            proNavigator.updateQueryParams({ id: threadId }, { reload: false });
            void queryClient.invalidateQueries({ queryKey: aiAgentQueryKeys.threads() });
        }
    }, [threadId]);

    const handleSubmitForm = () => {
        const payload = createFirstFormPayload();
        setFirstFormPayload(payload);
        AIAgentTrackingService.meeting.trackGenerateThread(payload.meetingContext.attendeesCount);
        onGenerate();
    };

    useEffect(() => {
        if (!companyData || !autogenerate || hasTriggeredAutogenerateRef.current || isLoading) {
            return;
        }
        const payload = createAutogeneratePayload();
        setFirstFormPayload(payload);
        hasTriggeredAutogenerateRef.current = true;
        AIAgentTrackingService.meeting.trackGenerateThread(payload.meetingContext.attendeesCount);
        onGenerate();
    }, [companyData, autogenerate, isLoading, createAutogeneratePayload, onGenerate]);

    const handleSubmitFeedback = (newReaction: FeedbackReaction) => {
        if (!threadId || !messageId || reaction === newReaction) {
            return;
        }

        AIAgentTrackingService.meeting.trackSubmitFeedback(companyName, newReaction);
        setReaction(newReaction);
        mutate({
            threadId,
            messageId,
            liked: newReaction === "like",
        });
    };

    const trackCopyClick = () => {
        AIAgentTrackingService.meeting.trackCopyBrief(companyName);
    };

    return (
        <ThemeProvider theme={CHAT_THEME["full-page"]}>
            <ChatMessage
                variant="company"
                companyName={companyName}
                companyIcon={companyData?.favicon}
            />
            <FirstRespondFormContent
                isDisabled={isDisabled}
                form={form}
                defaultCompetitors={defaultCompetitors}
            />

            {data && !isFetchedAfterMount ? (
                <ChatMessage
                    variant="ai"
                    content={data}
                    isLoading={isFetchingThreadGeneration}
                    onFeedback={handleSubmitFeedback}
                    reaction={reaction}
                    mdComponentProps={mdComponentProps}
                    withControls={false}
                    onCopy={trackCopyClick}
                />
            ) : null}
            {showAwaitingFirstAiBrief ? <AIAgentReasoning /> : null}
            {isFetchingThreadGeneration && data ? (
                <Box margin="-16px 0 0 35px">
                    <DotsLoader />
                </Box>
            ) : null}
            {isDisabled ? null : (
                <AIAgentChatFooter gradientToTop={!isButtonAnimationEnded}>
                    <StyledGradientButtonWrapper
                        justifyContent="center"
                        onAnimationEnd={() => setIsButtonAnimationEnded(true)}
                    >
                        <Tooltip
                            content={translate("si.pages.ai_agent.form.button.generate.no_company.tooltip")}
                            enabled={!domain}
                        >
                            <div>
                                <GradientButton
                                    variant="generate"
                                    onClick={handleSubmitForm}
                                    disabled={isLoading || !domain}
                                >
                                    {translate("si.pages.ai_agent.form.button.generate")}
                                </GradientButton>
                            </div>
                        </Tooltip>
                    </StyledGradientButtonWrapper>
                </AIAgentChatFooter>
            )}
        </ThemeProvider>
    );
};

export default FirstRespondForm;
